import { FC, useId, useMemo } from 'react';

import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';

import { Card, CardContent, CardFooter } from 'components/ui/card';
import { ChartContainer } from 'components/ui/chart';

import { DEFAULT_CHART_AXIS_TICK_STYLE } from '../analyticsChart.config';
import { AnalyticsLoadingBox } from '../AnalyticsLoadingBox';

import { IAnalyticsStackedTimeSeriesBoxProps } from './AnalyticsStackedTimeSeriesBox.types';
import {
  buildSegmentGradientId,
  CHART_HEIGHT_PX,
  EMPTY_CHART_CONFIG,
  formatCompactNumber,
  getXAxisLayout,
  mapTimeSeriesToChartData,
  resolveBarFill,
  resolveBarRadius
} from './AnalyticsStackedTimeSeriesBox.utils';
import { AnalyticsStackedTimeSeriesEmptyChartState } from './components/AnalyticsStackedTimeSeriesEmptyChartState';
import { AnalyticsStackedTimeSeriesHeader } from './components/AnalyticsStackedTimeSeriesHeader';
import { AnalyticsStackedTimeSeriesLegend } from './components/AnalyticsStackedTimeSeriesLegend';
import { AnalyticsStackedTimeSeriesTooltip } from './components/AnalyticsStackedTimeSeriesTooltip';

export const AnalyticsStackedTimeSeriesBox: FC<IAnalyticsStackedTimeSeriesBoxProps> = ({
  isLoading,
  title,
  tooltipInfo,
  primaryMetric,
  segments,
  timeSeries,
  valueFormatter,
  xAxisTickFormatter,
  xAxisTickAngle,
  tooltipTitleFormatter
}) => {
  const xAxisLayout = getXAxisLayout(xAxisTickAngle);
  const reactId = useId();
  const gradientIdPrefix = useMemo(
    () => `stacked-time-series-${reactId.replace(/:/g, '')}`,
    [reactId]
  );

  const chartData = useMemo(
    () => mapTimeSeriesToChartData(timeSeries),
    [timeSeries]
  );

  const hasChartData = chartData.length > 0;

  if (isLoading) {
    return <AnalyticsLoadingBox />;
  }

  return (
    <Card
      data-testid="analytics-stacked-time-series-box"
      className="flex flex-col w-full overflow-hidden p-0 gap-0 shadow-sm rounded-lg"
    >
      <AnalyticsStackedTimeSeriesHeader
        title={title}
        tooltipInfo={tooltipInfo}
        primaryMetric={primaryMetric}
        segments={segments}
        valueFormatter={valueFormatter}
      />

      <CardContent className="px-6 py-0">
        {!hasChartData && <AnalyticsStackedTimeSeriesEmptyChartState />}
        {hasChartData && (
          <ChartContainer
            config={EMPTY_CHART_CONFIG}
            style={{ height: CHART_HEIGHT_PX }}
            className="aspect-auto w-full"
          >
            <BarChart
              accessibilityLayer
              data={chartData}
              margin={{ top: 10, left: 0, right: 0, bottom: 0 }}
              barCategoryGap="20%"
            >
              <defs>
                {segments.map((segment) => {
                  if (!segment.gradient) {
                    return null;
                  }
                  const gradientId = buildSegmentGradientId(
                    gradientIdPrefix,
                    segment.key
                  );
                  return (
                    <linearGradient
                      key={gradientId}
                      id={gradientId}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={segment.gradient.topColor} />
                      <stop
                        offset="100%"
                        stopColor={segment.gradient.bottomColor}
                      />
                    </linearGradient>
                  );
                })}
              </defs>

              <CartesianGrid vertical={false} stroke="var(--base-border)" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tickFormatter={xAxisTickFormatter}
                tick={xAxisLayout.tickStyle}
                tickMargin={8}
                interval={0}
                angle={xAxisTickAngle}
                textAnchor={xAxisLayout.textAnchor}
                height={xAxisLayout.heightPx}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={formatCompactNumber}
                tick={DEFAULT_CHART_AXIS_TICK_STYLE}
                tickMargin={8}
                width={40}
              />
              <Tooltip
                content={
                  <AnalyticsStackedTimeSeriesTooltip
                    segments={segments}
                    valueFormatter={valueFormatter}
                    tooltipTitleFormatter={tooltipTitleFormatter}
                  />
                }
                cursor={{ fill: 'var(--base-muted)', opacity: 0.3 }}
                wrapperStyle={{ opacity: 1 }}
                isAnimationActive={false}
              />
              {segments.map((segment, index) => (
                <Bar
                  key={segment.key}
                  dataKey={segment.key}
                  stackId="stack"
                  fill={resolveBarFill(segment, gradientIdPrefix)}
                  radius={resolveBarRadius(index, segments.length)}
                />
              ))}
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>

      <CardFooter className="justify-center pt-4 pb-6">
        <AnalyticsStackedTimeSeriesLegend segments={segments} />
      </CardFooter>
    </Card>
  );
};
